import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Search, RefreshCw, Receipt, Building2, FileText,
  ChevronDown, ChevronRight, Edit, Trash2, Wallet,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import QuotationExpenseForm from '../../components/office/QuotationExpenseForm';
import OfficeExpenseForm from '../../components/office/OfficeExpenseForm';

const fmtDate = (d) => { if (!d) return '—'; try { return format(new Date(d), 'dd MMM yyyy'); } catch { return '—'; } };
const fmtINR = (n) => '₹' + (Number(n) || 0).toLocaleString('en-IN');

const ExpenseDashboard = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('quotation'); // 'quotation' | 'office'
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [showQForm, setShowQForm] = useState(false);
  const [qFormData, setQFormData] = useState(null);
  const [showOForm, setShowOForm] = useState(false);
  const [oFormData, setOFormData] = useState(null);

  const isAdmin = ['admin', 'super_admin', 'manager', 'accountant'].includes(user?.role);

  // ── Approved quotations (architecture) ──
  const { data: archQuotes = [], isLoading: la, refetch: ra } = useQuery({
    queryKey: ['approved_arch_quotations'],
    queryFn: () => pb.collection('quotations').getFullList({
      filter: 'approvalStatus = "approved"',
      sort: '-created',
    }).catch(() => []),
    enabled: !!user,
  });

  // ── Approved quotations (interior) ──
  const { data: intQuotes = [], isLoading: li, refetch: ri } = useQuery({
    queryKey: ['approved_int_quotations'],
    queryFn: () => pb.collection('interior_quotations').getFullList({
      sort: '-created',
    }).catch(() => []),
    enabled: !!user,
  });

  // ── Project expenses (against quotations) ──
  const { data: projExpenses = [], isLoading: lp, refetch: rp } = useQuery({
    queryKey: ['oc_project_expenses'],
    queryFn: () => pb.collection('oc_project_expenses').getFullList({
      sort: '-date,-created',
    }).catch(() => []),
    enabled: !!user,
  });

  // ── Office expenses ──
  const { data: officeExpenses = [], isLoading: lo, refetch: ro } = useQuery({
    queryKey: ['oc_office_expenses'],
    queryFn: () => pb.collection('oc_office_expenses').getFullList({
      sort: '-expenseDate,-created',
    }).catch(() => []),
    enabled: !!user,
  });

  const isLoading = la || li || lp || lo;

  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await Promise.all([ra(), ri(), rp(), ro()]); toast.success('Refreshed'); }
    catch { toast.error('Failed'); }
    finally { setTimeout(() => setRefreshing(false), 400); }
  };

  // Merge: each quotation with its expenses
  const quoteGroups = useMemo(() => {
    const list = [];

    archQuotes.forEach(q => {
      const exp = projExpenses.filter(e => e.quotationType === 'architecture' && e.quotation === q.id);
      list.push({
        key: `arch-${q.id}`,
        type: 'architecture',
        quotationId: q.id,
        quotationTypeField: 'quotation',
        clientName: q.clientName || 'Unknown',
        quoteNumber: q.quoteNumber || '—',
        total: Number(q.total) || 0,
        status: q.status,
        createdAt: q.created,
        expenses: exp,
        expenseTotal: exp.reduce((s, e) => s + (Number(e.amount) || 0), 0),
      });
    });

    intQuotes.forEach(q => {
      const exp = projExpenses.filter(e => e.quotationType === 'interior' && e.interiorQuotation === q.id);
      list.push({
        key: `int-${q.id}`,
        type: 'interior',
        quotationId: q.id,
        quotationTypeField: 'interiorQuotation',
        clientName: q.clientName || q.projectName || 'Unknown',
        quoteNumber: q.quotationNumber || q.quoteNumber || q.id.slice(0, 8),
        total: Number(q.total) || 0,
        status: q.status,
        createdAt: q.created,
        expenses: exp,
        expenseTotal: exp.reduce((s, e) => s + (Number(e.amount) || 0), 0),
      });
    });

    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [archQuotes, intQuotes, projExpenses]);

  const filteredGroups = useMemo(() => {
    if (!search) return quoteGroups;
    const q = search.toLowerCase();
    return quoteGroups.filter(g =>
      g.clientName.toLowerCase().includes(q) ||
      g.quoteNumber.toLowerCase().includes(q)
    );
  }, [quoteGroups, search]);

  const totalProjectExpense = useMemo(
    () => projExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [projExpenses]
  );
  const totalOfficeExpense = useMemo(
    () => officeExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [officeExpenses]
  );

  const toggleExpand = (key) => setExpandedQuotes(p => ({ ...p, [key]: !p[key] }));

  const deleteProjectExpense = async (e) => {
    if (!window.confirm(`Delete "${e.description}"?`)) return;
    try { await pb.collection('oc_project_expenses').delete(e.id); toast.success('Deleted'); refreshAll(); }
    catch (err) { toast.error(err.message); }
  };

  const deleteOfficeExpense = async (e) => {
    if (!window.confirm(`Delete "${e.description}"?`)) return;
    try { await pb.collection('oc_office_expenses').delete(e.id); toast.success('Deleted'); refreshAll(); }
    catch (err) { toast.error(err.message); }
  };

  const openQForm = (quotation) => {
    setQFormData(quotation);
    setShowQForm(true);
  };

  const openQFormEdit = (quotation, expense) => {
    setQFormData({ ...quotation, editingExpense: expense });
    setShowQForm(true);
  };

  const openOForm = (expense = null) => {
    setOFormData(expense);
    setShowOForm(true);
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
    </div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 sm:p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Receipt className="w-7 h-7 sm:w-8 sm:h-8" /> Expense Dashboard
            </h1>
            <p className="text-white/80 text-sm mt-1">Quotation-wise & office expenses</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshAll} disabled={refreshing}
              className="bg-white/10 text-white border-white/30 hover:bg-white/20">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative overflow-hidden bg-white rounded-2xl border-2 border-blue-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Quotation Expenses</div>
              <div className="text-2xl font-black text-slate-900 mt-0.5">{fmtINR(totalProjectExpense)}</div>
              <div className="text-xs text-slate-400 mt-0.5">{projExpenses.length} entries · {quoteGroups.length} quotations</div>
            </div>
          </div>
        </div>
        <div className="relative overflow-hidden bg-white rounded-2xl border-2 border-purple-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Office Expenses</div>
              <div className="text-2xl font-black text-slate-900 mt-0.5">{fmtINR(totalOfficeExpense)}</div>
              <div className="text-xs text-slate-400 mt-0.5">{officeExpenses.length} entries</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab('quotation')}
          className={`px-5 py-3 font-bold text-sm rounded-t-xl transition-all ${tab === 'quotation' ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-500 hover:text-slate-700'}`}>
          <FileText className="w-4 h-4 inline mr-2" />
          Quotation Expenses ({quoteGroups.length})
        </button>
        <button onClick={() => setTab('office')}
          className={`px-5 py-3 font-bold text-sm rounded-t-xl transition-all ${tab === 'office' ? 'text-purple-700 border-b-2 border-purple-600 bg-purple-50/50' : 'text-slate-500 hover:text-slate-700'}`}>
          <Building2 className="w-4 h-4 inline mr-2" />
          Office Expenses ({officeExpenses.length})
        </button>
      </div>

      {/* ═══════ QUOTATION EXPENSES TAB ═══════ */}
      {tab === 'quotation' && (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input type="text" placeholder="Search client or quotation..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none text-sm"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          {filteredGroups.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <FileText className="w-14 h-14 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No approved quotations found.</p>
              <p className="text-xs text-slate-400 mt-1">Approved quotations will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredGroups.map(g => {
                const isOpen = expandedQuotes[g.key];
                const typeBadge = g.type === 'architecture' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700';
                return (
                  <Card key={g.key} className="border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <CardContent className="p-0">
                      {/* Row */}
                      <div
                        onClick={() => toggleExpand(g.key)}
                        className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="text-slate-400 flex-shrink-0">
                            {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          </div>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded flex-shrink-0 ${typeBadge}`}>
                            {g.type === 'architecture' ? 'Arch' : 'Int'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-slate-800 truncate">{g.clientName}</div>
                            <div className="text-[11px] text-slate-500 font-mono">{g.quoteNumber} · {g.status}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-[10px] text-slate-500 uppercase font-semibold">Quote</div>
                            <div className="font-bold text-slate-800">{fmtINR(g.total)}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] text-slate-500 uppercase font-semibold">Expense</div>
                            <div className={`font-bold ${g.expenseTotal > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                              {fmtINR(g.expenseTotal)}
                            </div>
                          </div>
                          <div className="text-right hidden sm:block">
                            <div className="text-[10px] text-slate-500 uppercase font-semibold">Profit</div>
                            <div className={`font-bold ${g.total - g.expenseTotal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                              {fmtINR(g.total - g.expenseTotal)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isOpen && (
                        <div className="border-t border-slate-100 bg-slate-50 p-4">
                          <div className="flex justify-between items-center mb-3">
                            <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                              Expenses ({g.expenses.length})
                            </div>
                            {isAdmin && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openQForm(g); }}
                                className="text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition-all active:scale-95">
                                <Plus className="w-3.5 h-3.5 inline mr-1" /> Add Expense
                              </button>
                            )}
                          </div>

                          {g.expenses.length === 0 ? (
                            <div className="text-center py-6 text-slate-400 text-sm">
                              No expenses yet. Click "Add Expense" to record.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {g.expenses.map(e => (
                                <div key={e.id}
                                  className="bg-white rounded-lg border border-slate-200 p-3 flex items-start justify-between gap-3">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700 capitalize">
                                        {e.category}
                                      </span>
                                      <span className="text-[11px] text-slate-500">{fmtDate(e.date)}</span>
                                      {e.paidTo && <span className="text-[11px] text-slate-500">→ {e.paidTo}</span>}
                                    </div>
                                    <div className="text-sm font-medium text-slate-800 mt-1 truncate">{e.description}</div>
                                    {e.notes && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{e.notes}</div>}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="font-bold text-slate-900">{fmtINR(e.amount)}</div>
                                    {isAdmin && (
                                      <>
                                        <button onClick={(ev) => { ev.stopPropagation(); openQFormEdit(g, e); }}
                                          title="Edit"
                                          className="text-blue-500 hover:text-blue-700 p-1.5 rounded hover:bg-blue-100 transition">
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={(ev) => { ev.stopPropagation(); deleteProjectExpense(e); }}
                                          title="Delete"
                                          className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-100 transition">
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <div className="flex justify-end pt-2 border-t border-slate-200">
                                <div className="text-sm font-bold text-amber-700">
                                  Total: {fmtINR(g.expenseTotal)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════ OFFICE EXPENSES TAB ═══════ */}
      {tab === 'office' && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input type="text" placeholder="Search description or category..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {isAdmin && (
              <button onClick={() => openOForm(null)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-all active:scale-95 shadow-lg">
                <Plus className="w-4 h-4" /> Add Office Expense
              </button>
            )}
          </div>

          {officeExpenses.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <Building2 className="w-14 h-14 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No office expenses yet.</p>
              <p className="text-xs text-slate-400 mt-1">Add rent, salary, utilities etc.</p>
            </div>
          ) : (
            <Card className="border-0 shadow-xl overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gradient-to-r from-purple-50 to-fuchsia-50">
                    <tr>
                      <th className="p-3 text-left font-semibold text-purple-700">Date</th>
                      <th className="p-3 text-left font-semibold text-purple-700">Category</th>
                      <th className="p-3 text-left font-semibold text-purple-700">Description</th>
                      <th className="p-3 text-left font-semibold text-purple-700">Paid From</th>
                      <th className="p-3 text-right font-semibold text-purple-700">Amount</th>
                      <th className="p-3 text-center font-semibold text-purple-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {officeExpenses.map(e => (
                      <tr key={e.id} className="hover:bg-purple-50/50">
                        <td className="p-3 text-slate-600">{fmtDate(e.expenseDate || e.created)}</td>
                        <td className="p-3"><span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded capitalize">{e.category}</span></td>
                        <td className="p-3 max-w-[300px] truncate text-slate-700">{e.description}</td>
                        <td className="p-3 text-slate-500">{e.paidFrom || '—'}</td>
                        <td className="p-3 text-right font-bold text-slate-900">{fmtINR(e.amount)}</td>
                        <td className="p-3 text-center">
                          <div className="flex gap-1 justify-center">
                            {isAdmin && (
                              <>
                                <button onClick={() => openOForm(e)} title="Edit"
                                  className="text-purple-500 hover:text-purple-700 p-1.5 rounded hover:bg-purple-100 transition">
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button onClick={() => deleteOfficeExpense(e)} title="Delete"
                                  className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-100 transition">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-bold">
                    <tr>
                      <td colSpan={4} className="p-3 text-right text-slate-700">Total:</td>
                      <td className="p-3 text-right text-purple-700">{fmtINR(totalOfficeExpense)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modals */}
      {showQForm && qFormData && (
        <QuotationExpenseForm
          quotation={qFormData}
          editingExpense={qFormData.editingExpense || null}
          onClose={() => { setShowQForm(false); setQFormData(null); }}
          onSaved={() => { queryClient.invalidateQueries(['oc_project_expenses']); refreshAll(); }}
        />
      )}
      {showOForm && (
        <OfficeExpenseForm
          expense={oFormData}
          onClose={() => { setShowOForm(false); setOFormData(null); }}
          onSaved={() => { queryClient.invalidateQueries(['oc_office_expenses']); refreshAll(); }}
        />
      )}
    </div>
  );
};

export default ExpenseDashboard;
